import axios, { isCancel } from 'axios';
import { getCookie } from '../cookie-utils';
import { useGlobalErrorOverlay } from '../hooks';
import { i18n } from '../i18n-instance';
import { Languages } from '@/common/types';
import { getTokenPrefix } from './helper';
import { getIdToken } from './authHelper';

const axiosInstance = axios.create();

const { setError, clearError } = useGlobalErrorOverlay();
const { t } = i18n.global;

const validLanguages: Languages[] = [Languages['en-us'], Languages['de-de']];

function isValidLanguage(lang: string): lang is Languages {
  return validLanguages.includes(lang as Languages);
}

const failedQueue: (() => void)[] = [];

function processQueue() {
  while (failedQueue.length) {
    const retry = failedQueue.shift();
    retry?.();
  }
}

window.addEventListener('online', () => {
  clearError();
  processQueue();
});

window.addEventListener('offline', () => {
  setError(t('connectionLostDescription'), t('connectionLostTitle'));
});

axiosInstance.interceptors.request.use(
  async (config) => {
    const environment = getTokenPrefix();
    const url = config.url || '';

    const authToken = await getIdToken();
    if (authToken) {
      config.headers['x-user-auth'] = authToken;
    }
    const cookieLang = getCookie(`${environment}-locale`) as string;
    if (cookieLang && isValidLanguage(cookieLang)) {
      if (i18n.global.locale.value !== cookieLang) {
        i18n.global.locale.value = cookieLang;
      }
    }

    // Below is for requests with API Key
    let apiKeyCookieName;
    if (url.includes('.signals.')) {
      apiKeyCookieName = `${environment}-signalsApiKey`;
    } else if (url.includes('.events.')) {
      apiKeyCookieName = `${environment}-eventsApiKey`;
    } else if (url.includes('.reports.')) {
      apiKeyCookieName = `${environment}-reportsApiKey`;
    }

    if (apiKeyCookieName) {
      const apiKey = getCookie(apiKeyCookieName);
      if (apiKey) {
        config.headers['x-api-key'] = apiKey;
      }
    }

    const testAuthToken = getCookie('automated-test-auth-token');

    if (testAuthToken) {
      config.headers['x-user-auth'] = testAuthToken;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;

    const isJSONSignalCall = originalRequest.url.includes('json_signal');

    if (isCancel(error) || isJSONSignalCall) return;

    if (!navigator.onLine && !originalRequest._retryOnReconnect) {
      return new Promise((resolve, reject) => {
        failedQueue.push(() => {
          originalRequest._retryOnReconnect = true;
          axiosInstance(originalRequest).then(resolve).catch(reject);
        });
      });
    }

    const description = t(error.response?.data?.error?.code || '');

    if (error.response?.status === 400) {
      setError(description || t('errorAuthorization'), t('genericAccessError'));
    }

    if (error.response?.status === 403) {
      setError(description || t('errorAuthorization'), t('genericAccessError'));
    }

    if (error.response?.status === 404) {
      const title = t('resourceNotAvailable');
      const message = error?.response?.data?.error?.message || t('resourceNotAvailable');

      setError(description || message, title);
    }

    if (error.response?.data?.error?.code === 1000) {
      setError(description, t('genericAccessError'), true);
    }

    return Promise.reject(error);
  }
);

export { axiosInstance, failedQueue };
